<template>
    <DsModal
        :is-open="isOpen"
        show-close
        :title="$t('funnel.publishing.gadget.create.title')"
        size="sm"
        class="dashboard-widget-modal"
        @close="closeDashboardWidget"
    >
        <template #header>
            <DsFilledButton
                data-qa="create-widget-button"
                :disabled="hasNoGoals"
                @click="createWidget"
            >
                {{ $t('funnel.publishing.gadget.create.button') }}
            </DsFilledButton>
        </template>

        <template #default>
            <div v-if="hasWarnings">
                <DsInlineAlert leading-icon type="critical">
                    {{ $t('funnel.publishing.gadget.create.warning') }}
                </DsInlineAlert>
                <DsListItem
                    v-for="(warning, i) in warningMessages"
                    :key="i"
                    :title="warning.description"
                >
                    <template #leading>
                        <DsIcon name="alert-octagon" class="warning-widget-icon" />
                    </template>
                </DsListItem>
            </div>
            <div v-else class="dashboard-widget-description">
                {{ $t('funnel.publishing.gadget.create.description') }}
                <DsListItem
                    v-for="(goal, i) in goals"
                    :key="i"
                    data-qa="list-goal-item"
                    :title="goal.goalName"
                >
                    <template #leading>
                        <img class="funnel-goal-image" :src="getIcon(goal.strMetaType)" />
                    </template>
                </DsListItem>
            </div>
        </template>
    </DsModal>
</template>
<script>
import { getFunnelGadgetGoals, createFunnelWidget } from './create.dashboard.widget.api';
import { getIconPathByCellType } from '../../utils/cell/automation-display-utils';

export default {
    props: {
        isOpen: {
            type: Boolean,
            required: true,
        },
        publishedXml: {
            type: String,
            default: '',
        },
        funnelId: {
            type: Number,
            default: 0,
        },
        isPublished: {
            type: Boolean,
            required: true,
        },
    },
    emits: ['close-create-dashboard-widget'],
    data() {
        return {
            goals: [],
            warningMessages: [],
        };
    },

    computed: {
        hasNoGoals() {
            return this.goals.length === 0;
        },
        hasWarnings() {
            return this.warningMessages.length > 0;
        },
    },

    async mounted() {
        await this.load();
    },

    methods: {
        async load() {
            try {
                if (!this.isPublished) {
                    this.warningMessages.push({
                        id: 'hasBeenPublished',
                        description: this.$t('funnel.publishing.gadget.error.noPublish.checklistItem'),
                    });
                }

                if (this.publishedXml) {
                    this.goals = await getFunnelGadgetGoals(this.publishedXml);
                }

                if (this.hasNoGoals) {
                    this.warningMessages.push({
                        id: 'hasNoGoals',
                        description: this.$t('funnel.publishing.gadget.error.noGoal.checklistItem'),
                    });
                }
            } catch (error) {
                this.$error({ message: this.$t('funnel.publishing.gadget.loading.error') });
            }
        },

        async createWidget() {
            try {
                await createFunnelWidget(this.funnelId, this.publishedXml);
                this.$toast({ message: this.$t('funnel.publishing.gadget.creating.success') });
                this.closeDashboardWidget();
            } catch (error) {
                this.$error({ message: this.$t('funnel.publishing.gadget.creating.error') });
            }
        },

        getIcon(metaType) {
            return getIconPathByCellType(metaType);
        },

        closeDashboardWidget() {
            this.$emit('close-create-dashboard-widget');
        },
    },
};
</script>

<style lang="scss">
.dashboard-widget-modal {

    .modal-dialog.sm {
        max-width: 30rem;
        width: var(--modal-width, 30rem);
    }

    div.modal-body {
        padding: 0;
    }

    h4.ds-modal-header-title {
        font-size: 18px;
    }

    div.dashboard-widget-description {
        line-height: 1.5rem;
        font-size: 16px;
    }

    div.border-partial {
        padding-left: $spacing-150;
    }

    div.border-partial .list-item-info {
        padding-bottom: 0;
    }

    svg.warning-widget-icon {
        --icon-color: #{$color-red-500};
    }
}
img.funnel-goal-image {
    width: 40px;
    margin-right: 15px;
}

</style>
